import React from 'react';
import { Link } from 'react-router-dom';
import { Search, Shield, Globe, AlertTriangle, Lock, Eye } from 'lucide-react';
import { CodeBlock } from '../components/code/CodeBlock';

export const VerifyTransactionStatus: React.FC = () => {
  const headers = [
    { key: 'Authorization', value: '{PRIVATE_KEY}', required: true, description: 'The merchant\'s private key.' },
    { key: 'Content-Type', value: 'application/json', required: true, description: 'Specifies the media type of the resource.' }
  ];

  const pathParameters = [
    { name: 'transactionReference', type: 'string', required: true, description: 'The unique reference identifier of the transaction.' }
  ];

  const responseFields = [
    { name: 'transactionReference', type: 'string', description: 'A unique identifier assigned to the transaction.' },
    { name: 'merchantRef', type: 'string', description: 'The reference provided by the merchant during the transaction.' },
    { name: 'status', type: 'string', description: 'The current status of the transaction. Refer to Transaction Status table.' },
    { name: 'statusCode', type: 'integer', description: 'The numerical code representing the transaction status. Refer to Transaction Status table.' },
    { name: 'grossAmount', type: 'decimal', description: 'The total transaction amount, including fees (e.g., 100.00).' },
    { name: 'currencyCode', type: 'string', description: 'The currency used in the transaction (e.g., "USD", "NGN").' },
    { name: 'date', type: 'string', description: 'The date and time the transaction was initiated, formatted in ISO 8601 (YYYY-MM-DDTHH:MM:SSZ).' },
    { name: 'customerId', type: 'string', description: 'The unique identifier of the customer, which may include name and email.' }
  ];

  const errorCodes = [
    { code: '401', message: 'Unauthorized', description: 'Invalid merchant private key.' },
    { code: '403', message: 'Forbidden', description: 'IP address not whitelisted.' },
    { code: '404', message: 'Not Found', description: 'Transaction reference not found.' }
  ];

  const successResponseJson = `{
    "status": 200,
    "message": "Request successfully processed",
    "data": {
        "merchant": "Merchant 1",
        "transactionReference": "GjO2EBd_k1pu4mHmluXn",
        "merchantRef": "ecc0c53f-5130-4717-8ef1-1ac1ef02214c",
        "grossAmount": 50.75,
        "requestedAmount": 50.00,
        "transactionFee": 0.75,
        "settlementAmount": 49.25,
        "customerId": "example@gmail.com",
        "currencyCode": "NGN",
        "callbackUrl": "https://merchanturl.com",
        "authorizationType": "OPEN",
        "paymentRequestor": "Merchant Limited",
        "transactionDate": "2025-03-11T14:43:50.172275",
        "status": 2
    },
    "errors": []
}`;

  const error401Json = `{
  "status": "error",
  "message": "Unauthorized request. Please provide a valid merchant private key."
}`;

  const error403Json = `{
  "status": "error",
  "message": "Access denied. Your IP address is not whitelisted."
}`;

  const error404Json = `{
  "status": "error",
  "message": "Transaction not found for reference: TRX123456789."
}`;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-12">
        <h1 className="text-3xl font-bold text-white mb-4">Verify Transaction Status</h1>
        
        {/* Introduction */}
        <div className="mb-8">
          <p className="text-white-muted mb-6 leading-relaxed">
            This endpoint allows merchants to verify the status of a transaction using the unique transaction reference.
          </p>
          
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
            <div className="flex items-center mb-3">
              <Search className="h-5 w-5 text-blue-500 mr-2" />
              <h3 className="text-lg font-semibold text-blue-500">Transaction Verification</h3>
            </div>
            <ul className="text-sm text-blue-500/90 space-y-2">
              <li>• Real-time transaction status checking</li>
              <li>• Comprehensive transaction details retrieval</li>
              <li>• Secure server-to-server verification</li>
              <li>• Support for all transaction types</li>
            </ul>
          </div>
        </div>

        {/* Endpoint */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Endpoint</h2>
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex items-center">
              <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-mono mr-3">GET</span>
              <code className="text-lime font-mono">/transaction/verify/{'{transactionReference}'}</code>
            </div>
          </div>
        </div>

        {/* Headers */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Headers</h2>
          
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Key</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Value</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Required</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {headers.map((header, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{header.key}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{header.value}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Yes
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-white-muted">{header.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Path Parameters */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Path Parameters</h2>
          
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Parameter</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Required</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {pathParameters.map((param, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{param.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{param.type}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-white-muted">{param.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Request */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Request</h2>
          <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-6">
            <div className="flex items-center mb-2">
              <Eye className="h-5 w-5 text-gray-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-500">No Request Body</h3>
            </div>
            <p className="text-sm text-gray-500/90">
              There is no additional request body, as this endpoint primarily uses the path parameter and headers.
            </p>
          </div>
        </div>

        {/* Response Fields */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Response</h2>
          <p className="text-white-muted mb-4">
            A JSON response containing details of the transaction, including its current status.
          </p>
          
          <h3 className="text-lg font-semibold text-white mb-4">Fields in the transaction response:</h3>
          
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Field</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {responseFields.map((field, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-lime">{field.name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-white-muted">{field.type}</td>
                      <td className="px-4 py-4 text-sm text-white-muted">{field.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <h4 className="text-md font-semibold text-white mb-4">Response (Success):</h4>
          <CodeBlock code={successResponseJson} language="json" />
        </div>

        {/* Error Responses */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Error Responses</h2>
          
          <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Status Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white-muted uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {errorCodes.map((error, index) => (
                    <tr key={index} className="hover:bg-white/5">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-red-400">{error.code}</td>
                      <td className="px-4 py-4 text-sm text-white-muted">{error.message}</td>
                      <td className="px-4 py-4 text-sm text-white-muted">{error.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Example Error Responses */}
          <h3 className="text-lg font-semibold text-white mb-4">Example Error Responses:</h3>
          
          <div className="space-y-6">
            <div>
              <h4 className="text-md font-semibold text-red-400 mb-2">HTTP 401: Unauthorized</h4>
              <CodeBlock code={error401Json} language="json" />
            </div>
            
            <div>
              <h4 className="text-md font-semibold text-red-400 mb-2">HTTP 403: Forbidden</h4>
              <CodeBlock code={error403Json} language="json" />
            </div>
            
            <div>
              <h4 className="text-md font-semibold text-red-400 mb-2">HTTP 404: Not Found</h4>
              <CodeBlock code={error404Json} language="json" />
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Important Notes</h2>
          
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
              <div className="flex items-start">
                <Lock className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-red-500 mb-2">Private Key Security</h3>
                  <p className="text-sm text-red-500/90">
                    Ensure the private key is handled securely on your backend. Do not expose it on the client side 
                    (e.g., browser or mobile app).
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-6">
              <div className="flex items-start">
                <Shield className="h-5 w-5 text-orange-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-orange-500 mb-2">IP Whitelisting</h3>
                  <p className="text-sm text-orange-500/90">
                    The merchant's IP address must be whitelisted for the private key. Contact support to configure 
                    this if needed.
                  </p>
                </div>
              </div>
            </div>

            <Link to={'/server-to-server/status-code'} className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6 block">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-yellow-500 mb-2">Transaction Status Mapping</h3>
                  <p className="text-sm text-yellow-500/90">
                    Refer to the <Link to={'/server-to-server/status-code'} className='underline'>Transaction Status table</Link> for all possible statuses and their meanings.
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-lime text-dark rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <Globe className="h-6 w-6 mr-2" />
            <h2 className="text-xl font-bold">What's Next</h2>
          </div>
          <p className="text-dark/90 mb-4">
            Continue with transaction refund processing capabilities
          </p>
          <Link
            to="/server-to-server/refund"
            className="inline-flex items-center px-6 py-2 bg-dark text-white font-medium rounded-lg hover:bg-dark/90 transition-colors"
          >
            Refund
          </Link>
        </div>
      </div>
    </div>
  );
};